import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { validateToken, logout, selectUserRole, selectUser } from "./features/authSlice";
import ProtectedRoute from "./components/ProtectedRoute";
import { setFavicon } from "./hooks/useBranding";
import "./App.css";

// Pages
import ExamHome from "./pages/ExamHome";
import Login from "./pages/Login";
const ExamHistory = React.lazy(() => import("./pages/ExamHistory"));
const ExamLeaderboard = React.lazy(() => import("./pages/ExamLeaderboard"));
const ExamCompetitions = React.lazy(() => import("./pages/ExamCompetitions"));
const ExamCompetitionDetail = React.lazy(() => import("./pages/ExamCompetitionDetail"));
const AdminMembers = React.lazy(() => import("./pages/AdminMembers"));
const ExamResultDetail = React.lazy(() => import("./pages/ExamResultDetail"));

// Generic Exam Adapter (works for any exam in EXAM_REGISTRY)
const ExamAdapter = React.lazy(() => import("./pages/ExamAdapter"));
const AdminReviews = React.lazy(() => import("./pages/AdminReviews"));

// ─── SuperAdmin Home ────────────────────────────────────────────────
function SuperAdminHome() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-y-auto">
      <header className="sticky top-0 z-50 bg-white text-slate-800 shadow-lg border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br from-blue-500 to-blue-700">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              </div>
              <div>
                <h1 className="font-bold text-xl leading-tight text-slate-800">Skillform</h1>
                <p className="text-sm text-slate-500">Admin Console</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-all"
              title="Sign out"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/admin/reviews')}
            className="flex flex-col items-center gap-4 bg-white rounded-2xl border border-slate-200 p-10 hover:border-slate-300 hover:shadow-md transition-all active:scale-[0.99]"
          >
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <div className="text-center">
              <p className="font-bold text-slate-800 text-xl">Review Queue</p>
              <p className="text-base text-slate-400 mt-1">Review AI-flagged evaluations</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/exams')}
            className="flex flex-col items-center gap-4 bg-white rounded-2xl border border-slate-200 p-10 hover:border-slate-300 hover:shadow-md transition-all active:scale-[0.99]"
          >
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                <path d="M6 12v5c3 3 9 3 12 0v-5"/>
              </svg>
            </div>
            <div className="text-center">
              <p className="font-bold text-slate-800 text-xl">Exam Simulator</p>
              <p className="text-base text-slate-400 mt-1">Take practice exams</p>
            </div>
          </button>
        </div>
      </main>
    </div>
  );
}

// ─── Home Router ────────────────────────────────────────────────────
function HomeRouter() {
  const role = useSelector(selectUserRole);

  if (role === 'evaluator') return <AdminReviews />;
  if (role === 'superadmin') return <SuperAdminHome />;
  // student, admin → exam picker
  return <ExamHome />;
}

function App() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Validate existing token on mount
  useEffect(() => {
    dispatch(validateToken());
  }, [dispatch]);

  // Set favicon to org's small logo when authenticated
  useEffect(() => {
    const logoSmall = user?.organization?.logo_small;
    if (logoSmall) setFavicon(logoSmall);
  }, [user]);

  return (
    <React.Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />

        {/* Protected — role-based home */}
        <Route path="/" element={<ProtectedRoute><HomeRouter /></ProtectedRoute>} />

        {/* Exam routes — students, admins (teachers), superadmins */}
        <Route path="/exams" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamHome /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamHistory /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamLeaderboard /></ProtectedRoute>} />
        <Route path="/competitions" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamCompetitions /></ProtectedRoute>} />
        <Route path="/competitions/:competitionId" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamCompetitionDetail /></ProtectedRoute>} />
        <Route path="/history/:examId" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamResultDetail /></ProtectedRoute>} />
        <Route path="/exam/:examId" element={<ProtectedRoute allowedRoles={['student', 'admin', 'superadmin']}><ExamAdapter /></ProtectedRoute>} />

        {/* Admin routes */}
        <Route path="/admin/members" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><AdminMembers /></ProtectedRoute>} />
        <Route path="/admin/reviews" element={<ProtectedRoute allowedRoles={['evaluator', 'superadmin']}><AdminReviews /></ProtectedRoute>} />
        <Route path="/admin/reviews/:skillScoreId" element={<ProtectedRoute allowedRoles={['evaluator', 'superadmin']}><AdminReviews /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

export default App;
